import React, { useEffect, useRef, useState } from "react";
import {
  Upload,
  Button,
  Spin,
  Typography,
  Card,
  message,
  Row,
  Col,
  Modal,
  Form,
  Input,
  Select,
} from "antd";
import { UploadOutlined, FileSearchOutlined } from "@ant-design/icons";
import analyzerService from "../../services/analyzer";
import { useAuth } from "../../contexts/AuthContext";

const { Title, Text } = Typography;

const EyeHealthAnalyzer = () => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [reportMessage, setReportMessage] = useState(null);
  const [file, setFile] = useState(null);

  const [supplementMsg, setSupplementMsg] = useState(null);
  const [supplementLoader, setSupplementLoader] = useState(false);

  const [aiText, setAiText] = useState("");
  const [imgPath, setImgPath] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState("user");
  const [cameraError, setCameraError] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [patientDetails, setPatientDetails] = useState({
    name: "",
    phone: "",
    gender: "",
    email: "",
  });

  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [detailsForm] = Form.useForm();
  const detailsComplete = Boolean(
    patientDetails.name &&
      patientDetails.phone &&
      patientDetails.gender &&
      patientDetails.email
  );

  const buildPatientPayload = () => {
    const recipientEmails = Array.from(
      new Set([patientDetails.email, user?.email].filter(Boolean))
    );

    return {
      name: patientDetails.name,
      fullName: patientDetails.name,
      phone: patientDetails.phone,
      phoneNo: patientDetails.phone,
      gender: patientDetails.gender,
      email: patientDetails.email,
      loggedInEmail: user?.email || "",
      userEmail: user?.email || "",
      recipientEmails,
    };
  };

  const openDetailsModal = () => {
    detailsForm.setFieldsValue(patientDetails);
    setDetailsModalOpen(true);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async (facingMode = cameraFacingMode) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera is not supported on this device/browser.");
      return;
    }

    setCameraLoading(true);
    setCameraError("");
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facingMode } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraOpen(true);
    } catch (error) {
      console.error("Camera access error:", error);
      setCameraError("Unable to access camera. Please allow permission and try again.");
      setCameraOpen(false);
    } finally {
      setCameraLoading(false);
    }
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
  };

  useEffect(() => {
    setPatientDetails((prev) => ({
      name: prev.name || user?.fullName || user?.name || "",
      phone: prev.phone || user?.phone || "",
      gender: prev.gender || user?.gender || "",
      email: prev.email || user?.email || "",
    }));
  }, [user?.email, user?.fullName, user?.gender, user?.name, user?.phone]);

  const flipCamera = async () => {
    const nextMode = cameraFacingMode === "user" ? "environment" : "user";
    setCameraFacingMode(nextMode);
    await startCamera(nextMode);
  };

  const captureFromCamera = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          message.error("Failed to capture image. Please try again.");
          return;
        }

        if (imageUrl) {
          URL.revokeObjectURL(imageUrl);
        }

        const capturedFile = new File([blob], `eye-capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        setFile(capturedFile);
        setImageUrl(URL.createObjectURL(blob));
        setReportMessage(null);
        setSupplementMsg(null);
        openDetailsModal();
        closeCamera();
      },
      "image/jpeg",
      0.95
    );
  };

  useEffect(() => {
    return () => {
      stopCamera();
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleAnalyze = async () => {
    if (!file) {
      message.warning("Please upload or capture an eye image first.");
      return;
    }
    if (!detailsComplete) {
      message.warning("Please add customer details before analysis.");
      openDetailsModal();
      return;
    }

    setLoading(true);
    setReportMessage("");
    try {
      const response = await analyzerService.uploadImage(file, buildPatientPayload());

      if (response?.success) {
        message.success("Analysis complete!");
        setImgPath(response.urlPath);
        setAiText(response.aiText);
        setReportMessage(response.message);
      } else {
        message.error("Analysis failed. Try again.");
        setImageUrl(null);
      }
    } catch (err) {
      console.error(err);
      message.error("Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async ({ file }) => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(URL.createObjectURL(file));
    setFile(file);
    setReportMessage(null);
    setSupplementMsg(null);
    openDetailsModal();
  };

  const handleGetSupplements = async () => {
    if (!detailsComplete) {
      message.warning("Please add customer details before requesting supplements.");
      openDetailsModal();
      return;
    }

    setSupplementLoader(true);
    try {
      const response = await analyzerService.getSupplements(
        imgPath,
        aiText,
        buildPatientPayload()
      );

      if (response.success) {
        setSupplementMsg(response.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSupplementLoader(false);
    }
  };

  const handleSaveDetails = async () => {
    try {
      const values = await detailsForm.validateFields();
      setPatientDetails(values);
      setDetailsModalOpen(false);
      message.success("Customer details saved.");
    } catch (error) {
      // antd form shows field errors
    }
  };

  return (
    <div className="bg-white min-h-[calc(100vh-90px)] px-10 py-8">
      <div className="mb-8">
        <Title level={3} className="!m-0 !text-[#1c5a85] font-semibold">
          Eye on Health Analyzer
        </Title>
        <Text className="text-gray-500 text-sm">
          Upload or capture an eye image to get instant analysis - precise, safe,
          and private.
        </Text>
      </div>

      <Row gutter={24} align="middle">
        <Col xs={24} md={12}>
          <Card
            bordered={false}
            className="border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-all p-8"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spin size="large" className="text-[#1c5a85]" />
                <Text className="text-gray-500 mt-3 text-sm">
                  Analyzing image...
                </Text>
              </div>
            ) : (
              <>
                {!imageUrl ? (
                  <div className="flex flex-col items-center justify-center text-center space-y-5">
                    <Upload
                      accept="image/*"
                      customRequest={handleUpload}
                      showUploadList={false}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        className="!bg-[#1c5a85] !text-white !border-none !rounded-md px-6 hover:!bg-[#174b6e]"
                      >
                        Upload Eye Image
                      </Button>
                    </Upload>
                    <Text className="text-gray-400 text-xs">
                      Supported formats: JPG, PNG - up to 10MB
                    </Text>

                    <Button
                      onClick={() => startCamera(cameraFacingMode)}
                      loading={cameraLoading}
                      className="!rounded-md px-6"
                    >
                      Open Camera
                    </Button>

                    {cameraError ? (
                      <Text type="danger" className="text-xs">
                        {cameraError}
                      </Text>
                    ) : null}

                    {cameraOpen ? (
                      <div className="w-full flex flex-col items-center gap-3 pt-2">
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-72 h-72 object-cover rounded-xl border border-gray-200 bg-black"
                        />
                        <canvas ref={canvasRef} className="hidden" />
                        <div className="flex gap-2">
                          <Button onClick={flipCamera}>Flip Camera</Button>
                          <Button
                            type="primary"
                            className="!bg-[#1c5a85] !border-none"
                            onClick={captureFromCamera}
                          >
                            Capture
                          </Button>
                          <Button danger onClick={closeCamera}>
                            Close
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <img
                      src={imageUrl}
                      alt="Uploaded eye"
                      className="w-56 h-56 object-cover rounded-xl border border-gray-200 mb-5"
                    />
                    <div className="w-full max-w-md border border-[#e8f1f7] rounded-md px-4 py-3 mb-4 text-sm">
                      <div className="flex items-center justify-between mb-2">
                        <Text strong className="!text-[#1c5a85]">
                          Customer Details
                        </Text>
                        <Button size="small" onClick={openDetailsModal}>
                          Edit
                        </Button>
                      </div>
                      {detailsComplete ? (
                        <div className="grid grid-cols-1 gap-y-1 text-gray-600">
                          <Text className="!text-sm">
                            Name: {patientDetails.name}
                          </Text>
                          <Text className="!text-sm">
                            Phone: {patientDetails.phone}
                          </Text>
                          <Text className="!text-sm">
                            Gender: {patientDetails.gender}
                          </Text>
                          <Text className="!text-sm">
                            Email: {patientDetails.email}
                          </Text>
                        </div>
                      ) : (
                        <Text type="danger" className="!text-xs">
                          Add Name, Phone, Gender and Email to continue.
                        </Text>
                      )}
                    </div>
                    <div className="flex justify-between items-center gap-x-2">
                      <Button
                        type="primary"
                        className="!bg-[#1c5a85] !border-none !rounded-md px-8 hover:!bg-[#174b6e]"
                        onClick={handleAnalyze}
                        disabled={supplementLoader || !detailsComplete}
                      >
                        Analyze Image
                      </Button>
                      <Button
                        type="primary"
                        className="!bg-[#1c5a85] !border-none !rounded-md px-8 hover:!bg-[#174b6e]"
                        onClick={() => {
                          if (imageUrl) {
                            URL.revokeObjectURL(imageUrl);
                          }
                          setFile(null);
                          setImageUrl(null);
                          setReportMessage(null);
                          setSupplementMsg(null);
                        }}
                        disabled={supplementLoader}
                      >
                        Remove Image
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12} className=" flex flex-col gap-y-2 items-center">
          <Card
            bordered={false}
            className="border border-gray-100 rounded-xl shadow-sm p-8 h-full"
          >
            {supplementLoader ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spin size="large" className="text-[#1c5a85]" />
                <Text className="text-gray-500 mt-3 text-sm">
                  Generating supplements...
                </Text>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <FileSearchOutlined className="text-[#1c5a85] text-2xl" />
                  <Title level={4} className="!m-0 !text-[#1c5a85]">
                    Analysis Report
                  </Title>
                </div>

                <div className="text-gray-500 text-sm leading-relaxed">
                  {loading ? (
                    <p>Processing your eye image...</p>
                  ) : imageUrl ? (
                    <>
                      {reportMessage ? (
                        <>
                          <div className="mb-3 rounded-md border border-[#e8f1f7] bg-[#f8fcff] p-3">
                            <p className="mb-1">
                              <span className="font-medium text-[#1c5a85]">Name:</span>{" "}
                              {patientDetails.name || "N/A"}
                            </p>
                            <p className="mb-1">
                              <span className="font-medium text-[#1c5a85]">Phone:</span>{" "}
                              {patientDetails.phone || "N/A"}
                            </p>
                            <p className="mb-1">
                              <span className="font-medium text-[#1c5a85]">Gender:</span>{" "}
                              {patientDetails.gender || "N/A"}
                            </p>
                            <p>
                              <span className="font-medium text-[#1c5a85]">Email:</span>{" "}
                              {patientDetails.email || "N/A"}
                            </p>
                          </div>
                          {supplementMsg ? (
                            <>
                              {reportMessage} <br /> {supplementMsg}
                            </>
                          ) : (
                            reportMessage
                          )}
                        </>
                      ) : (
                        <>
                          <p>
                            <span className="font-medium text-[#1c5a85]">
                              Status:
                            </span>{" "}
                            {"Click Analyze Image to start analysis..."}
                          </p>
                          <p className="mt-2">
                            Once uploaded, the system evaluates your eye image
                            for possible indicators of:
                          </p>
                          <ul className="list-disc pl-5 mt-2">
                            <li>Diabetic Retinopathy</li>
                            <li>Macular Degeneration</li>
                            <li>Glaucoma</li>
                          </ul>
                        </>
                      )}
                    </>
                  ) : (
                    <p>
                      No image uploaded yet. Upload an eye image to begin
                      image analysis. Your image is processed
                      securely and not stored.
                    </p>
                  )}
                </div>
              </>
            )}
          </Card>
          {imageUrl && loading === false && reportMessage && (
            <Button
              className="!bg-[#1c5a85]/10 !border !border-[#1c5a85] !text-[#1c5a85] !rounded-md px-6 hover:!bg-[#174b6e] hover:!text-white w-[98%] "
              onClick={handleGetSupplements}
              disabled={supplementLoader}
            >
              Request Supplements
            </Button>
          )}
        </Col>
      </Row>

      <Modal
        open={detailsModalOpen}
        title="Customer Details"
        onCancel={() => setDetailsModalOpen(false)}
        onOk={handleSaveDetails}
        okText="Save Details"
        cancelText="Cancel"
        maskClosable={false}
      >
        <Form form={detailsForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Please enter name." }]}
          >
            <Input placeholder="Enter customer name" />
          </Form.Item>

          <Form.Item
            label="Phone No"
            name="phone"
            rules={[{ required: true, message: "Please enter phone number." }]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            label="Gender"
            name="gender"
            rules={[{ required: true, message: "Please select gender." }]}
          >
            <Select
              placeholder="Select gender"
              options={[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
                { value: "Other", label: "Other" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              { required: true, message: "Please enter email address." },
              { type: "email", message: "Please enter a valid email." },
            ]}
          >
            <Input placeholder="Enter report email" />
          </Form.Item>
        </Form>
      </Modal>

      <div className="text-gray-400 text-xs mt-8 text-center">
        &copy; 2025 <span className="text-[#1c5a85] font-medium">Eye on Health</span>{" "}
      </div>
    </div>
  );
};

export default EyeHealthAnalyzer;
